import { forwardRef, useImperativeHandle, useRef, useState, type KeyboardEvent } from 'react';
import { ChevronDown, FileText, MessageSquare, Pencil, Plus, Send, Square } from 'lucide-react';
import { FEATURES } from '../config/features';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/atoms/DropdownMenu';
import { Textarea } from '../components/atoms/Textarea';
import { cn } from '../lib/utils';
import type { ChatMode } from './types';

/**
 * Public handle: parent uses this to imperatively focus/clear the input
 * without owning text state.
 */
export interface ChatInputHandle {
  focus: () => void;
  clear: () => void;
  setValue: (v: string) => void;
}

interface Props {
  /** Called with the current text when the user hits Send / Enter. */
  onSend: (text: string) => void;
  onStop?: () => void;
  disabled?: boolean;
  mode: ChatMode;
  onModeChange: (m: ChatMode) => void;
  isStreaming?: boolean;
  /** Optional text pinned above the input as context (e.g. currently selected text in Word) */
  contextPreview?: string;
  onClearContext?: () => void;
  placeholder?: string;
}

const MODE_OPTIONS: Array<{
  value: ChatMode;
  label: string;
  icon: typeof MessageSquare;
  description: string;
}> = [
  {
    value: 'research',
    label: 'Research',
    icon: MessageSquare,
    description: 'Research case law, statutes, and legal concepts',
  },
  {
    value: 'edit',
    label: 'Edit',
    icon: Pencil,
    description: 'Edit the document based on instructions',
  },
  // Draft mode — experimental. `FEATURES.draftMode` is a compile-time
  // constant; Vite strips this entry when the flag is false.
  ...(FEATURES.draftMode
    ? ([
        {
          value: 'draft',
          label: 'Draft',
          icon: FileText,
          description: 'Generate a new legal document from a brief',
        },
      ] as const)
    : []),
];

/**
 * Task pane chat input.
 *
 * Owns its own text state so typing doesn't cascade re-renders up to the
 * parent (which in turn would re-render the entire message list). Mirrors
 * the "input state lives in its own Redux slice" pattern from frontend-prod:
 * the parent doesn't see the value change until the user actually sends.
 *
 * Parent interacts through an imperative ref (focus / clear / setValue)
 * when it needs to push a value in (e.g. restoring a draft, wiping on
 * thread switch) — rare cases, not keystroke-frequent.
 */
export const TaskPaneChatInput = forwardRef<ChatInputHandle, Props>(function TaskPaneChatInput(
  {
    onSend,
    onStop,
    disabled,
    mode,
    onModeChange,
    isStreaming,
    contextPreview,
    onClearContext,
    placeholder,
  },
  ref,
): JSX.Element {
  const [value, setValue] = useState('');
  const taRef = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => taRef.current?.focus(),
      clear: () => setValue(''),
      setValue: (v: string) => setValue(v),
    }),
    [],
  );

  const active = MODE_OPTIONS.find((o) => o.value === mode) ?? MODE_OPTIONS[0];
  const ActiveIcon = active.icon;
  const canSend = value.trim().length > 0 && !disabled;

  function submit() {
    const text = value.trim();
    if (!text || disabled) return;
    setValue('');
    onSend(text);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) submit();
    }
  }

  const defaultPlaceholder =
    mode === 'research'
      ? 'Ask a legal question or research case law…'
      : 'Describe how you want to edit this document…';

  return (
    <div className="shrink-0 border-t border-border bg-background px-3 pt-2 pb-3">
      {contextPreview && (
        <div className="mb-2 flex items-start gap-2 rounded-md bg-muted/60 px-2 py-1.5 text-xs text-muted-foreground">
          <span className="italic truncate flex-1" title={contextPreview}>
            “{contextPreview.slice(0, 140)}{contextPreview.length > 140 ? '…' : ''}”
          </span>
          {onClearContext && (
            <button
              type="button"
              onClick={onClearContext}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Clear selection context"
            >
              ×
            </button>
          )}
        </div>
      )}

      <div className="rounded-lg border border-border focus-within:ring-2 focus-within:ring-ring transition-shadow">
        <Textarea
          ref={taRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder ?? defaultPlaceholder}
          maxHeight={180}
          className="border-0 focus:ring-0"
        />

        <div className="flex items-center justify-between px-1.5 pb-1.5 pt-0.5">
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs font-medium text-foreground hover:bg-muted/80 transition-colors"
                  aria-label="Choose mode"
                >
                  <ActiveIcon className="w-3.5 h-3.5" />
                  <span>{active.label}</span>
                  <ChevronDown className="w-3 h-3 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                {MODE_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <DropdownMenuItem
                      key={opt.value}
                      onClick={() => onModeChange(opt.value)}
                      className={cn(opt.value === mode && 'bg-accent')}
                    >
                      <Icon className="w-4 h-4 mt-0.5 text-foreground" />
                      <div className="flex flex-col">
                        <span className="font-medium text-foreground">{opt.label}</span>
                        <span className="text-xs text-muted-foreground">{opt.description}</span>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              type="button"
              disabled
              className="inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground opacity-50 cursor-not-allowed"
              title="Attachments (coming soon)"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center">
            {isStreaming && onStop ? (
              <button
                type="button"
                onClick={onStop}
                className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-muted text-foreground hover:bg-muted/80"
                aria-label="Stop generation"
                title="Stop"
              >
                <Square className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={!canSend}
                className={cn(
                  'inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors',
                  canSend
                    ? 'bg-foreground text-background hover:bg-foreground/90'
                    : 'bg-muted text-muted-foreground cursor-not-allowed',
                )}
                aria-label="Send message"
                title="Send"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
